import React, { useMemo, useRef, useState } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera, Html, Billboard, Line } from '@react-three/drei';
import * as THREE from 'three';

const Node = ({ node, onClick, isSelected }) => {
  const meshRef = useRef();
  const [hovered, setHovered] = useState(false);

  useFrame(() => {
    if (meshRef.current && !isSelected) {
      meshRef.current.rotation.y += 0.01;
    }
  });

  const handlePointerOver = (event) => {
    event.stopPropagation();
    setHovered(true);
    document.body.style.cursor = 'pointer';
  };

  const handlePointerOut = (event) => {
    event.stopPropagation();
    setHovered(false);
    document.body.style.cursor = 'default';
  };

  const handleClick = (event) => {
    event.stopPropagation();
    onClick(node);
  };

  const showTooltip = hovered || isSelected;

  return (
    <group position={[node.renderX, node.renderY, node.renderZ]}>
      <mesh
        ref={meshRef}
        onClick={handleClick}
        onPointerOver={handlePointerOver}
        onPointerOut={handlePointerOut}
      >
        <sphereGeometry args={[node.radius + (node.isHighlighted ? 0.08 : 0), 24, 24]} />
        <meshStandardMaterial
          color={node.color}
          emissive={node.isHighlighted ? node.color : '#12192c'}
          emissiveIntensity={node.isHighlighted ? 0.9 : 0.25}
          transparent
          opacity={Math.max(node.opacity, 0.45)}
          roughness={0.35}
          metalness={0.1}
        />
      </mesh>

      {node.isHighlighted && (
        <Billboard>
          <mesh>
            <ringGeometry args={[node.radius * 1.55, node.radius * 1.95, 48]} />
            <meshBasicMaterial
              color="#ffd86b"
              transparent
              opacity={0.6}
              blending={THREE.AdditiveBlending}
              depthWrite={false}
            />
          </mesh>
        </Billboard>
      )}

      {showTooltip && (
        <Html
          distanceFactor={8}
          style={{
            pointerEvents: 'none',
            background: 'rgba(6, 10, 18, 0.95)',
            border: '1px solid rgba(122, 168, 255, 0.35)',
            color: '#f2f6ff',
            padding: '12px 14px',
            borderRadius: '10px',
            maxWidth: '320px',
            whiteSpace: 'normal',
            fontSize: '11px',
            lineHeight: '1.5',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)'
          }}
        >
          <div style={{ marginBottom: '8px', borderBottom: '1px solid rgba(122, 168, 255, 0.2)', paddingBottom: '6px' }}>
            <strong style={{ fontSize: '13px', color: node.color || '#7595ff' }}>
              {node.agent || node.type || 'Node'}
            </strong>
            {node.model && (
              <div style={{ fontSize: '9px', color: '#a0a4b8', marginTop: '2px' }}>
                Model: {node.model}
              </div>
            )}
          </div>
          
          <div style={{ marginBottom: '6px', fontSize: '10px', color: '#d0d4e8' }}>
            {node.snippet || node.msg?.slice(0, 200) || 'No content'}
          </div>
          
          {(node.tools_used && node.tools_used.length > 0) && (
            <div style={{ marginTop: '6px', paddingTop: '6px', borderTop: '1px solid rgba(122, 168, 255, 0.15)' }}>
              <div style={{ fontSize: '9px', color: '#9ca3af', marginBottom: '3px' }}>Tools:</div>
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                {node.tools_used.map((tool, i) => (
                  <span key={i} style={{ 
                    fontSize: '9px', 
                    background: 'rgba(58, 208, 222, 0.2)', 
                    color: '#3ad0de', 
                    padding: '2px 6px', 
                    borderRadius: '4px',
                    border: '1px solid rgba(58, 208, 222, 0.3)'
                  }}>
                    {tool}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          <div style={{ marginTop: '8px', paddingTop: '6px', borderTop: '1px solid rgba(122, 168, 255, 0.15)', fontSize: '9px', color: '#9ca3af', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {node.execution_time && (
              <span> {node.execution_time}</span>
            )}
            {node.cost && (
              <span> {node.cost}</span>
            )}
            {node.tokens && (
              <span> {node.tokens} tokens</span>
            )}
            {node.timestamp && (
              <span> {new Date(node.timestamp).toLocaleDateString()}</span>
            )}
          </div>
          
          {node.run_id && (
            <div style={{ marginTop: '6px', fontSize: '8px', color: '#6b7280', fontFamily: 'monospace' }}>
              ID: {node.run_id.slice(0, 12)}...
            </div>
          )}
        </Html>
      )}
    </group>
  );
};

const Scene = ({ nodes, selectedNode, onNodeClick, edges, showEdges, showDensity, onPositionsUpdate }) => {
  const { camera, size } = useThree();
  const frameCountRef = useRef(0);

  useFrame(() => {
    frameCountRef.current += 1;
    if (!onPositionsUpdate) return;
    // Throttle screen position updates
    if (frameCountRef.current % 6 !== 0) return;

    const positions = {};
    nodes.forEach((node) => {
      const vector = new THREE.Vector3(node.renderX, node.renderY, node.renderZ);
      vector.project(camera);
      positions[node.id] = {
        x: (vector.x * 0.5 + 0.5) * size.width,
        y: (-vector.y * 0.5 + 0.5) * size.height
      };
    });
    onPositionsUpdate(positions);
  });

  const densityPositions = useMemo(() => {
    const array = new Float32Array(nodes.length * 3);
    nodes.forEach((node, idx) => {
      array[idx * 3] = node.renderX;
      array[idx * 3 + 1] = node.renderY;
      array[idx * 3 + 2] = node.renderZ;
    });
    return array;
  }, [nodes]);

  const edgeCurves = useMemo(() => {
    if (!showEdges || !edges || edges.length === 0) return [];

    return edges.map((edge) => {
      const start = new THREE.Vector3(edge.source.renderX, edge.source.renderY, edge.source.renderZ);
      const end = new THREE.Vector3(edge.target.renderX, edge.target.renderY, edge.target.renderZ);
      const mid = start.clone().add(end).multiplyScalar(0.5);
      const arcHeight = 0.22 + 0.05 * ((edge.source.cluster || 0) - (edge.target.cluster || 0));
      mid.y += arcHeight;
      const curve = new THREE.CatmullRomCurve3([start, mid, end]);
      const points = curve.getPoints(24);
      return {
        id: edge.id,
        points,
        highlighted: edge.highlighted
      };
    });
  }, [edges, showEdges]);

  return (
    <>
      <PerspectiveCamera makeDefault position={[6, 6, 6]} />
      {/* No background color - let it be transparent */}

      <OrbitControls enableDamping dampingFactor={0.06} />

      <ambientLight intensity={0.6} />
      <directionalLight position={[12, 16, 10]} intensity={0.7} />
      <pointLight position={[-12, -14, -10]} intensity={0.5} />

      {showDensity && nodes.length > 0 && (
        <points>
          <bufferGeometry>
            <bufferAttribute
              attachObject={['attributes', 'position']}
              count={nodes.length}
              itemSize={3}
              array={densityPositions}
            />
          </bufferGeometry>
          <pointsMaterial size={0.05} transparent opacity={0.08} color="#83aaff" depthWrite={false} />
        </points>
      )}

      {showEdges && edgeCurves.map((edge) => (
        <Line
          key={edge.id}
          points={edge.points}
          color={edge.highlighted ? '#ffd86b' : '#ffffff30'}
          lineWidth={edge.highlighted ? 1.6 : 0.6}
          dashed={!edge.highlighted}
          dashSize={0.12}
          gapSize={0.08}
        />
      ))}

      {nodes.map((node) => (
        <Node
          key={node.id}
          node={node}
          onClick={onNodeClick}
          isSelected={selectedNode?.id === node.id}
        />
      ))}
    </>
  );
};

export default Scene;
